import React, { useEffect, useMemo, useState } from 'react';
import { fetchToken } from '@agissc/ktag-map';
import { ExternalViewerAPI } from './types';
import useAppState from '../AppStore';
import { ExternalViewerAPIContext } from './ExternalViewerAPIContext';
import { ToolNames } from '../tools/ToolNames';
import { MapContentViews } from '../tools/MapContentViews';
import ZoomToAttributeSearch from '../tools/mapContent/topics/attributeSearch/ZoomToAttributeSearch';
import { DEFAULT_TOOL } from '../tools/Tools';

export const ExternalViewerAPIProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const addNotification = useAppState((state) => state.addNotification);
  const isLoggedIn = useAppState((state) => state.isLoggedIn);
  const [loginExecuted, setLoginExecuted] = useState(false);
  const [attributeSearchLayer, setAttributeSearchLayer] = useState('');

  const showNotification = (msg: string, title: string) => {
    addNotification({
      id: Date.now().toString(),
      title,
      message: msg,
      type: 'information',
    });
  };

  const getToken = async () => {
    try {
      return await fetchToken();
    } catch {
      return null;
    }
  };

  const api = useMemo<ExternalViewerAPI>(
    () => ({
      login: async () => {
        const token = await getToken();
        if (token) {
          setLoginExecuted(true);
        } else {
          document.getElementById('header__control--userinfo')?.click();
        }
      },
      setLayers: (layers: string[]) => {
        const state = useAppState.getState();
        state.setSelectedTopicLayers(layers);
      },
      setBasemap: (basemap: string) => {
        const state = useAppState.getState();
        state.setSelectedBasemapId(basemap);
      },

      find: (query: {
        serviceId: string;
        layerId: number;
        attribute: string;
        query: string;
      }) => {
        const state = useAppState.getState();
        state.setCurrentTopicLayerId(query.serviceId);
        state.setActiveTool(ToolNames.layerSelection);
        state.setActiveView(MapContentViews.topicAttributeSearch);
        state.setAttributeSearch(query.serviceId, {
          sublayerId: query.layerId,
          query: query.query,
          attribute: query.attribute,
          draftQuery: query.query,
          selectedFeatureId: undefined,
          highlightedFeatureId: undefined,
        });
        state.setCurrentAttributeSearchTopicId(query.serviceId);

        setAttributeSearchLayer(query.serviceId);
      },
      clearFindResults: () => {
        const state = useAppState.getState();
        state.setActiveTool(DEFAULT_TOOL);
      },
      selectLocation: (callback: (result: { x: number; y: number }) => void) => {
        const unsubscribe = useAppState.subscribe(
          (state) => state.identifyCoordinate,
          (coordinate) => {
            if (coordinate) {
              callback({ x: coordinate[0], y: coordinate[1] });
              unsubscribe();
            }
          },
        );

        const state = useAppState.getState();
        if (!state.enabledIdentify) state.toggleIdentify();
      },
      showNotification: (msg: string, title: string) => showNotification(msg, title),
      zoomToLocation: (x: number, y: number, zoom = 7) => {
        const state = useAppState.getState();
        const location = { ...state.location, center: { x, y }, zoom };
        state.setLocation(location);
      },
    }),
    [addNotification],
  );

  useEffect(() => {
    window.av4 = window.av4 || { viewer: {} };
    window.av4.viewer.externalAPI = window.av4.viewer.externalAPI || {};

    window.av4.viewer.externalAPI.getViewer = (
      callback: (viewer: ExternalViewerAPI) => void,
    ) => {
      if (api) {
        callback(api);
      }
    };

    return () => {
      delete window.av4?.viewer?.externalAPI?.getViewer;
    };
  }, [api]);

  useEffect(() => {
    if (api === null) return;
    if (!loginExecuted) return;

    if (isLoggedIn) {
      api?.onLogin?.();
    }
  }, [api, isLoggedIn, loginExecuted]);

  return (
    <ExternalViewerAPIContext.Provider value={api}>
      {attributeSearchLayer > ' ' && (
        <ZoomToAttributeSearch
          topicId={attributeSearchLayer}
          callback={() => {
            setAttributeSearchLayer('');
          }}
        />
      )}
      {children}
    </ExternalViewerAPIContext.Provider>
  );
};
